import { QueryClient } from '@tanstack/react-query';

export enum HttpResponseStatus {
  OK = 200,
  CREATED = 201,
  ACCEPTED = 202,
  NO_CONTENT = 204,
  NOT_MODIFIED = 304,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  CONFLICT = 409,
  INTERNAL_SERVER_ERROR = 500,
}

/**
 * Represents an HTTP response with structured data
 */
export interface HttpResponse<T> extends Response {
  data?: T;
}

interface RequestArgs {
  info: RequestInfo;
  init?: RequestInit;
}

/**
 * A request interceptor is a function that is called before a request is made to the backend.
 * It can alter the request configuration in order, for example, to add extra parameters.
 */
export type RequestInterceptor = (info: RequestInfo, init?: RequestInit) => Promise<RequestArgs>;

/**
 * A response interceptor is called after a response is received from the backend.
 * It can alter the response data or perform actions based on the response status.
 */
export type ResponseInterceptor = <T = unknown>(
  response: HttpResponse<T>
) => Promise<HttpResponse<T>>;

const requestInterceptors: RequestInterceptor[] = [];

export class RequestFailed extends Error {
  response: HttpResponse<any>;

  constructor(response: HttpResponse<any>) {
    super(response.data?.message || `Request failed: ${response.status} ${response.statusText}`);
    this.response = response;
  }
}

/**
 * Adds a request interceptor to process all requests made using the http function
 * @param interceptor The request interceptor
 * @returns A function that can remove the newly added interceptor
 */
export function addRequestInterceptor(interceptor: RequestInterceptor) {
  requestInterceptors.push(interceptor);
  return () => {
    requestInterceptors.splice(requestInterceptors.indexOf(interceptor), 1);
  };
}

const responseInterceptors: ResponseInterceptor[] = [];

/**
 * Adds a response interceptor to process all responses made using the http function
 * @param interceptor The response interceptor
 * @returns A function that can remove the newly added interceptor
 */
export function addResponseInterceptor(interceptor: ResponseInterceptor) {
  responseInterceptors.push(interceptor);
  return () => {};
}

/**
 * A wrapper for the native fetch function that adds strong typing to the data and executes request and response interceptors
 */
export async function http<T>(info: RequestInfo, init?: RequestInit, returnRaw?: boolean) {
  let options: RequestArgs = { info, init };

  for (const interceptor of requestInterceptors) {
    options = await interceptor(options.info, options.init);
  }

  const BINARY_RESPONSE = /application\/x-(gzip|tgz)/;

  let response: HttpResponse<T> = await fetch(options.info, options.init);

  // The interceptor has a default behavior of parsing the response as JSON
  // we need to check if the response is a tarball, and not parse it
  // We also need an additional check if the response has a 204 status code or other no-content status before parsing
  if (
    response.status === 204 ||
    response.headers.get('Content-Type')?.match(BINARY_RESPONSE) ||
    response.headers.get('Content-Length') === '0' ||
    returnRaw
  ) {
    // No content returned so do not attempt to parse the response
  } else {
    try {
      response.data = await response.json();
    } catch (e) {
      console.error('network interceptor: failed to parse response as json', e);
    }
  }

  for (const interceptor of responseInterceptors) {
    response = await interceptor(response);
  }

  return response;
}

/**
 * Returns the data of successful responses, or throws RequestFailed for failed responses.
 */
export function verifyResponse<T>(response: HttpResponse<T>) {
  if (!response.ok || !response.data) {
    throw new RequestFailed(response);
  }
  return response.data;
}

export function verifyResponseStatus<T>(response: HttpResponse<T>) {
  if (!response.ok) {
    throw new RequestFailed(response);
  }
  return response;
}

/**
 * "Serving suggestion" for using react-query with http functions.
 * Clients can use their own query client with care, but this one is provided
 * with some reasonable defaults as a convenience.
 */
export const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, staleTime: 500 } },
});
